import React from "react";
import "./IntegrationTicket.scss";
import { useTranslation } from "react-i18next";
import { TextButton } from "../../design-system/controls";
import { Link, useRouteMatch } from "react-router-dom";
import {
  mapIntegrationCategory,
  PUBLIC_THREAT_INTELLIGENCE,
  mapIntegrationName,
} from "../DataSources/DataSourcesUtils";
import IntegrationTicketContent from "./IntegrationTicketContent";
import { Label } from "../../design-system/components";
import { useIcon } from "../../design-system/assets/useIcon";

// TODO: This is a local boundary type for the subset of fields the component
// reads. The app passes `DataSourceDefinitionWithInstances`
// (src/AppSettings/Integrations/DataSources/DataSourcesUtils.tsx), which cannot
// be imported into packages/shared. `iconName` is read here but is absent from
// that interface — align the two when the data shape is hoisted into shared.
export interface IntegrationTicketData {
  category?: string;
  name?: string;
  iconName?: string;
  id?: string;
  unavailable?: boolean;
  hasAtLeastOneInstance?: boolean;
}

export interface IntegrationTicketProps {
  disabled?: boolean;
  data: IntegrationTicketData;
  mutateAllowed?: boolean;
  readOnly?: boolean;
  children?: React.ReactNode;
  showCategory?: boolean;
}

const IntegrationTicket: React.FC<IntegrationTicketProps> = ({
  disabled = false,
  data,
  mutateAllowed = true,
  readOnly = false,
  children,
  showCategory = false,
}) => {
  const { path } = useRouteMatch();
  const { t } = useTranslation();
  const { getDataSourceIcon } = useIcon();

  return (
    <div className={`integrations-ticket-wrap ${!!disabled && "is-disabled"}`}>
      <div className="integrations-ticket-header">
        {!!showCategory && (
          <Label
            text={mapIntegrationCategory(data.category)}
            className="integration-category"
          />
        )}
        <div
          className={`integrations-ticket-img`}
          style={{
            backgroundImage: `url("${getDataSourceIcon(data?.iconName || data?.name, "L")}")`,
          }}
        />
        <div className={`integrations-ticket-source`}>
          {mapIntegrationName(data?.name)}
        </div>
      </div>

      {!!children ? (
        <IntegrationTicketContent>{children}</IntegrationTicketContent>
      ) : (
        <></>
      )}

      <div className="integrations-ticket-footer">
        {data.category === PUBLIC_THREAT_INTELLIGENCE && (
          <div
            className={"integrations-readonly-footer integrated-by-seemplicity"}
          >
            Integrated by Seemplicity
          </div>
        )}
        {!readOnly ? (
          Boolean(data.unavailable && !data.hasAtLeastOneInstance) ? (
            <div
              className={
                "integrations-readonly-footer integrations-readonly-footer--unavailable"
              }
              data-testid={"open-integration"}
            >
              {t("Contact Support to Integrate")}
            </div>
          ) : !data?.hasAtLeastOneInstance ? (
            <Link to={`${path}/${data?.id}/new`}>
              <TextButton
                data-testid={`open-integration-${data?.id}`}
                buttonStyle={"textLink"}
                withTooltip={
                  !mutateAllowed
                    ? {
                        content:
                          "Access to this action is restricted to Admins",
                      }
                    : undefined
                }
                disabled={!mutateAllowed}
                wrapperClassName={"integration-button is-blue"}
                label={"Integrate"}
                type={"button"}
                data-analytics-event="user_click"
                data-analytics-props={`{"name": "integrate_btn", "integration": "${data?.name}"}`}
                onClick={(e) => {
                  if (!mutateAllowed) {
                    e.stopPropagation();
                  }
                }}
              />
            </Link>
          ) : (
            <Link to={`${path}/${data?.id}`}>
              <TextButton
                data-testid={"view-integration"}
                buttonStyle={"textLink"}
                label={"View Integration"}
                wrapperClassName={"integration-button"}
                type={"button"}
              />
            </Link>
          )
        ) : Boolean(data.unavailable) ? (
          <div
            className={
              "integrations-readonly-footer integrations-readonly-footer--unavailable"
            }
          >
            Coming soon
          </div>
        ) : (
          <div className={"integrations-readonly-footer"}>Integrated</div>
        )}
      </div>
    </div>
  );
};

export default IntegrationTicket;
